import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Camera, MapPin, Loader2, CheckCircle2, UploadCloud, AlertTriangle, X, Info, Phone, Mail, ChevronRight } from 'lucide-react';
// Kategori sesuai dokumen
const ISSUE_CATEGORIES = [{
  value: 'tumpukan',
  label: 'Tumpukan Sampah',
  description: 'Sampah menumpuk di tempat tidak semestinya'
}, {
  value: 'pencemaran',
  label: 'Pencemaran',
  description: 'Pencemaran air, tanah, atau udara'
}, {
  value: 'lainnya',
  label: 'Lainnya',
  description: 'Isu lingkungan lainnya'
}];
export function LaporIsuPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    contactType: '',
    contact: ''
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleGetLocation = () => {
    setLocationStatus('loading');
    if (!navigator.geolocation) {
      setLocationStatus('error');
      setErrors({
        ...errors,
        location: 'Browser tidak mendukung GPS'
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      setFormData({
        ...formData,
        latitude: latitude.toFixed(6),
        longitude: longitude.toFixed(6),
        location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
      });
      setLocationStatus('success');
      setErrors({
        ...errors,
        location: ''
      });
    }, error => {
      setLocationStatus('error');
      let errorMsg = 'Gagal mendapatkan lokasi';
      if (error.code === 1) errorMsg = 'Izin lokasi ditolak';
      if (error.code === 2) errorMsg = 'Lokasi tidak tersedia';
      if (error.code === 3) errorMsg = 'Timeout mendapatkan lokasi';
      setErrors({
        ...errors,
        location: errorMsg
      });
    }, {
      enableHighAccuracy: true,
      timeout: 10000
    });
  };
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors({
          ...errors,
          photo: 'Ukuran file maksimal 10MB'
        });
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors({
          ...errors,
          photo: 'File harus berupa gambar'
        });
        return;
      }
      setPhotoFile(file);
      setErrors({
        ...errors,
        photo: ''
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.category) {
      newErrors.category = 'Pilih kategori isu';
    }
    if (!photoFile) {
      newErrors.photo = 'Foto bukti wajib diupload';
    }
    if (!formData.location || !formData.latitude || !formData.longitude) {
      newErrors.location = 'Lokasi wajib diisi (gunakan GPS atau input manual)';
    }
    if (!formData.description || formData.description.length < 20) {
      newErrors.description = 'Deskripsi minimal 20 karakter';
    }
    if (formData.contactType && !formData.contact) {
      newErrors.contact = 'Masukkan nomor/email untuk notifikasi';
    }
    if (formData.contactType === 'whatsapp' && formData.contact) {
      const phoneRegex = /^(\+62|62|0)8[1-9][0-9]{6,10}$/;
      if (!phoneRegex.test(formData.contact.replace(/\s/g, ''))) {
        newErrors.contact = 'Format nomor WhatsApp tidak valid';
      }
    }
    if (formData.contactType === 'email' && formData.contact) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact)) {
        newErrors.contact = 'Format email tidak valid';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Simulate API call with geotagged data
    // In real app: POST /public/issues with FormData including photo, lat/lng, timestamp
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('success');
    }, 2000);
  };
  const ticketId = 'TKT-2024-' + Math.floor(1000 + Math.random() * 9000);
  return <div className="min-h-screen bg-background flex flex-col font-sans text-foreground" data-id="element-4624">
      <Header data-id="element-4625" />

      <main className="flex-1 bg-gray-50 py-8 md:py-12" data-id="element-4626">
        <div className="container mx-auto px-4 md:px-6 max-w-2xl" data-id="element-4627">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-id="element-4628">
            <Link to="/" className="hover:text-primary-600" data-id="element-4629">
              Beranda
            </Link>
            <ChevronRight className="h-4 w-4" data-id="element-4630" />
            <span className="text-gray-900 font-medium" data-id="element-4631">Lapor Isu</span>
          </nav>

          {step === 'form' ? <>
              <div className="text-center mb-8" data-id="element-4632">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-600 mb-4" data-id="element-4633">
                  <AlertTriangle className="h-8 w-8" data-id="element-4634" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4635">
                  Lapor Isu Lingkungan
                </h1>
                <p className="text-gray-600" data-id="element-4636">
                  Laporkan tumpukan sampah, pencemaran, atau isu lingkungan
                  lainnya di sekitar Anda.
                </p>
              </div>

              <Card className="border-0 shadow-lg overflow-hidden" data-id="element-4637">
                <div className="h-2 bg-gradient-to-r from-red-500 to-orange-500 w-full" data-id="element-4638" />
                <CardContent className="p-6 md:p-8" data-id="element-4639">
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4640">
                    {/* Kategori */}
                    <div className="space-y-2" data-id="element-4641">
                      <label className="text-sm font-medium text-gray-900" data-id="element-4642">
                        Kategori Isu <span className="text-red-500" data-id="element-4643">*</span>
                      </label>
                      <div className="grid grid-cols-1 gap-3" data-id="element-4644">
                        {ISSUE_CATEGORIES.map(cat => <button key={cat.value} type="button" onClick={() => {
                      setFormData({
                        ...formData,
                        category: cat.value
                      });
                      setErrors({
                        ...errors,
                        category: ''
                      });
                    }} className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${formData.category === cat.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4645">
                            <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${formData.category === cat.value ? 'border-primary-500 bg-primary-500' : 'border-gray-300'}`} data-id="element-4646">
                              {formData.category === cat.value && <CheckCircle2 className="h-3 w-3 text-white" data-id="element-4647" />}
                            </div>
                            <div data-id="element-4648">
                              <p className="font-medium text-gray-900" data-id="element-4649">
                                {cat.label}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-4650">
                                {cat.description}
                              </p>
                            </div>
                          </button>)}
                      </div>
                      {errors.category && <p className="text-sm text-red-600 flex items-center gap-1" data-id="element-4651">
                          <AlertTriangle className="h-4 w-4" data-id="element-4652" />
                          {errors.category}
                        </p>}
                    </div>

                    {/* Foto */}
                    <div className="space-y-2" data-id="element-4653">
                      <label className="text-sm font-medium text-gray-900" data-id="element-4654">
                        Foto Bukti <span className="text-red-500" data-id="element-4655">*</span>
                      </label>
                      <div className={`border-2 border-dashed rounded-lg transition-colors relative ${errors.photo ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'}`} data-id="element-4656">
                        {photoPreview ? <div className="p-4" data-id="element-4657">
                            <div className="relative inline-block" data-id="element-4658">
                              <img src={photoPreview} alt="Preview" className="h-48 object-cover rounded-md" data-id="element-4659" />
                              <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600" data-id="element-4660">
                                <X className="h-4 w-4" data-id="element-4661" />
                              </button>
                            </div>
                            <p className="text-sm text-green-600 mt-2 flex items-center gap-1" data-id="element-4662">
                              <CheckCircle2 className="h-4 w-4" data-id="element-4663" />
                              Foto berhasil diupload
                            </p>
                          </div> : <div className="p-6 text-center cursor-pointer" data-id="element-4664">
                            <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" accept="image/*" capture="environment" onChange={handlePhotoUpload} data-id="element-4665" />
                            <Camera className="mx-auto h-12 w-12 text-gray-400 mb-2" data-id="element-4666" />
                            <p className="text-sm font-medium text-gray-700" data-id="element-4667">
                              Ambil Foto atau Upload
                            </p>
                            <p className="text-xs text-gray-500 mt-1" data-id="element-4668">
                              PNG, JPG (Max. 10MB)
                            </p>
                          </div>}
                      </div>
                      {errors.photo && <p className="text-sm text-red-600 flex items-center gap-1" data-id="element-4669">
                          <AlertTriangle className="h-4 w-4" data-id="element-4670" />
                          {errors.photo}
                        </p>}
                    </div>

                    {/* Lokasi */}
                    <div className="space-y-2" data-id="element-4671">
                      <label className="text-sm font-medium text-gray-900" data-id="element-4672">
                        Lokasi Kejadian <span className="text-red-500" data-id="element-4673">*</span>
                      </label>
                      <div className="flex gap-2" data-id="element-4674">
                        <Input placeholder="Koordinat lokasi (lat, lng)" value={formData.location} onChange={e => {
                      setFormData({
                        ...formData,
                        location: e.target.value
                      });
                      setErrors({
                        ...errors,
                        location: ''
                      });
                    }} className={`h-12 ${errors.location ? 'border-red-300' : ''}`} data-id="element-4675" />
                        <Button type="button" variant="outline" className={`h-12 px-4 ${locationStatus === 'success' ? 'border-green-300 text-green-700 bg-green-50' : 'border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100'}`} onClick={handleGetLocation} disabled={locationStatus === 'loading'} data-id="element-4676">
                          {locationStatus === 'loading' ? <Loader2 className="h-5 w-5 animate-spin" data-id="element-4677" /> : locationStatus === 'success' ? <CheckCircle2 className="h-5 w-5" data-id="element-4678" /> : <MapPin className="h-5 w-5" data-id="element-4679" />}
                        </Button>
                      </div>
                      {locationStatus === 'success' && <p className="text-xs text-green-600 flex items-center gap-1" data-id="element-4680">
                          <CheckCircle2 className="h-3 w-3" data-id="element-4681" />
                          Lokasi GPS berhasil didapatkan (akurasi tinggi)
                        </p>}
                      {errors.location && <p className="text-sm text-red-600 flex items-center gap-1" data-id="element-4682">
                          <AlertTriangle className="h-4 w-4" data-id="element-4683" />
                          {errors.location}
                        </p>}
                      <p className="text-xs text-gray-500" data-id="element-4684">
                        Klik tombol GPS atau masukkan koordinat manual
                      </p>
                    </div>

                    {/* Deskripsi */}
                    <div className="space-y-2" data-id="element-4685">
                      <label className="text-sm font-medium text-gray-900" data-id="element-4686">
                        Deskripsi Singkat{' '}
                        <span className="text-red-500" data-id="element-4687">*</span>
                      </label>
                      <Textarea placeholder="Jelaskan kondisi di lapangan (minimal 20 karakter)..." className={`min-h-[120px] text-base ${errors.description ? 'border-red-300' : ''}`} value={formData.description} onChange={e => {
                    setFormData({
                      ...formData,
                      description: e.target.value
                    });
                    if (e.target.value.length >= 20) {
                      setErrors({
                        ...errors,
                        description: ''
                      });
                    }
                  }} data-id="element-4688" />
                      <div className="flex justify-between" data-id="element-4689">
                        {errors.description ? <p className="text-sm text-red-600 flex items-center gap-1" data-id="element-4690">
                            <AlertTriangle className="h-4 w-4" data-id="element-4691" />
                            {errors.description}
                          </p> : <span data-id="element-4692" />}
                        <p className={`text-xs ${formData.description.length < 20 ? 'text-orange-500' : 'text-green-600'}`} data-id="element-4693">
                          {formData.description.length}/20 karakter minimum
                        </p>
                      </div>
                    </div>

                    {/* Kontak (Opsional) */}
                    <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-4694">
                      <div className="flex items-start gap-2" data-id="element-4695">
                        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4696" />
                        <div data-id="element-4697">
                          <p className="text-sm font-medium text-blue-900" data-id="element-4698">
                            Ingin mendapat notifikasi status? (Opsional)
                          </p>
                          <p className="text-xs text-blue-700 mt-1" data-id="element-4699">
                            Masukkan kontak untuk menerima update penanganan
                            laporan Anda.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2" data-id="element-4700">
                        <button type="button" onClick={() => setFormData({
                      ...formData,
                      contactType: formData.contactType === 'whatsapp' ? '' : 'whatsapp',
                      contact: ''
                    })} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${formData.contactType === 'whatsapp' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`} data-id="element-4701">
                          <Phone className="h-4 w-4" data-id="element-4702" />
                          WhatsApp
                        </button>
                        <button type="button" onClick={() => setFormData({
                      ...formData,
                      contactType: formData.contactType === 'email' ? '' : 'email',
                      contact: ''
                    })} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${formData.contactType === 'email' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`} data-id="element-4703">
                          <Mail className="h-4 w-4" data-id="element-4704" />
                          Email
                        </button>
                      </div>

                      {formData.contactType && <Input placeholder={formData.contactType === 'whatsapp' ? '08xxxxxxxxxx' : 'email@example.com'} type={formData.contactType === 'email' ? 'email' : 'tel'} value={formData.contact} onChange={e => {
                    setFormData({
                      ...formData,
                      contact: e.target.value
                    });
                    setErrors({
                      ...errors,
                      contact: ''
                    });
                  }} className={`bg-white ${errors.contact ? 'border-red-300' : ''}`} data-id="element-4705" />}
                      {errors.contact && <p className="text-sm text-red-600 flex items-center gap-1" data-id="element-4706">
                          <AlertTriangle className="h-4 w-4" data-id="element-4707" />
                          {errors.contact}
                        </p>}
                    </div>

                    <div className="pt-4" data-id="element-4708">
                      <Button type="submit" className="w-full h-12 text-lg bg-red-600 hover:bg-red-700 font-semibold shadow-md" disabled={isSubmitting} data-id="element-4709">
                        {isSubmitting ? <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" data-id="element-4710" />
                            Mengirim Laporan...
                          </> : <>
                            <UploadCloud className="mr-2 h-5 w-5" data-id="element-4711" />
                            Kirim Laporan
                          </>}
                      </Button>
                      <p className="text-xs text-center text-gray-500 mt-4" data-id="element-4712">
                        Dengan mengirim laporan, Anda menyetujui data lokasi dan
                        foto diproses untuk penanganan isu lingkungan.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </> : <div className="text-center py-8 animate-in fade-in zoom-in duration-500" data-id="element-4713">
              <div className="h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4714">
                <CheckCircle2 className="h-12 w-12 text-green-600" data-id="element-4715" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4716">
                Laporan Diterima!
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto" data-id="element-4717">
                Terima kasih atas partisipasi Anda. Laporan telah masuk ke
                sistem dan akan segera diverifikasi.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-sm mx-auto mb-8 shadow-sm" data-id="element-4718">
                <p className="text-sm text-gray-500 mb-1" data-id="element-4719">Nomor Tiket Anda</p>
                <p className="text-2xl font-mono font-bold text-primary-700 tracking-wider" data-id="element-4720">
                  {ticketId}
                </p>
                <p className="text-xs text-gray-400 mt-2" data-id="element-4721">
                  Simpan nomor ini untuk melacak status laporan
                </p>
              </div>

              {/* Timeline Status */}
              <div className="max-w-md mx-auto mb-10 px-4" data-id="element-4722">
                <div className="relative flex justify-between items-center" data-id="element-4723">
                  <div className="absolute top-4 left-0 w-full h-1 bg-gray-200 -z-10" data-id="element-4724"></div>
                  <div className="absolute top-4 left-0 w-1/4 h-1 bg-primary-600 -z-10" data-id="element-4725"></div>

                  {['Diterima', 'Verifikasi', 'Dispatch', 'Penanganan', 'Selesai'].map((label, i) => <div key={label} className="flex flex-col items-center bg-gray-50 px-1 z-10" data-id="element-4726">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ring-4 ring-white ${i === 0 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-4727">
                        {i + 1}
                      </div>
                      <p className={`text-[10px] font-medium mt-2 ${i === 0 ? 'text-primary-700' : 'text-gray-500'}`} data-id="element-4728">
                        {label}
                      </p>
                    </div>)}
                </div>
              </div>

              {formData.contactType && formData.contact && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm mx-auto mb-8" data-id="element-4729">
                  <p className="text-sm text-blue-800" data-id="element-4730">
                    Notifikasi akan dikirim ke{' '}
                    <strong data-id="element-4731">{formData.contact}</strong>
                  </p>
                </div>}

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4732">
                <Link to={`/track-laporan`} data-id="element-4733">
                  <Button variant="outline" className="h-12 px-8 w-full sm:w-auto" data-id="element-4734">
                    Lacak Status Laporan
                  </Button>
                </Link>
                <Button className="h-12 px-8 bg-primary-600 hover:bg-primary-700" onClick={() => {
              setStep('form');
              setPhotoPreview(null);
              setPhotoFile(null);
              setFormData({
                category: '',
                description: '',
                location: '',
                latitude: '',
                longitude: '',
                contactType: '',
                contact: ''
              });
              setLocationStatus('idle');
              setErrors({});
            }} data-id="element-4735">
                  Laporkan Lagi
                </Button>
              </div>
            </div>}
        </div>
      </main>

      <Footer data-id="element-4736" />
    </div>;
}